import { IdPrefix, parsePublicId } from '@lib/api-types';
import type { RoadmapView as ViewPayload } from '@lib/api-types/views';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';

import {
  useCreatePlan,
  useCreateRoadmap,
  useDeletePlan,
  useRepositories,
  useUpdatePlan,
  useUpdateRoadmap,
} from '../../api.ts';
import { ControlPanel } from '../components/ControlPanel.tsx';
import { ConnectReposEmpty, EmptyState } from '../components/EmptyState.tsx';
import {
  FloatingToolbar,
  GroupSkillsIcon,
  ShowLibrariesIcon,
  ToolbarButton,
  ToolbarSegmented,
  ToolbarToggle,
} from '../components/FloatingToolbar.tsx';
import { ErrorView, Skeleton } from '../components/Skeleton.tsx';
import { buildRatingIndex, indexSkills, type RatingValue } from '../data-views.ts';
import { useRoadmapView, useViewQueryState } from '../hooks/useViews.ts';
import {
  aggregateProjects,
  activeRoadmap,
  applyPlanCompOverlay,
  buildPlanProjectViews,
  buildPlanProjectedRosters,
  buildProjectViews,
  buildProjectedRosters,
  initialRoadmapState,
  projectionContext,
  roadmapReducer,
  talentResourceProjection,
  type ProjectedTalentColumn,
  type ProjectView,
} from '../roadmap-data.ts';

import type { OverlayOption } from './roadmap/OverlayPicker.tsx';
import {
  applyPatchToProjectPayload,
  applyPatchToTeamChangePayload,
  computeLinkedPatch,
} from './roadmap/propagate-links.ts';
import {
  buildSkillToComps,
  normalizeLegacyReqMap,
  reqKey,
} from './roadmap/req-key.ts';
import { RoadmapDiffGrid } from './roadmap/RoadmapDiffGrid.tsx';
import { RoadmapImpactGrid } from './roadmap/RoadmapImpactGrid.tsx';
import { RoadmapResourceGrid } from './roadmap/RoadmapResourceGrid.tsx';
import { RoadmapSidebar } from './roadmap/RoadmapSidebar.tsx';

const SUBTITLE = 'Project planning & feasibility.';

export function RoadmapView() {
  const { data: view, isLoading, error } = useRoadmapView();
  if (error) {
    return <ErrorView title='Roadmap' error={error} />;
  }
  if (isLoading || !view) {
    return <Skeleton title='Roadmap' subtitle={SUBTITLE} rows={8} />;
  }
  return <RoadmapBody view={view} />;
}

function RoadmapBody({ view }: { view: ViewPayload }) {
  const { useMock, slug: orgSlug } = useViewQueryState();
  // Mirror Timeline's empty state: a fresh org with no linked repos gets
  // the repo-connect prompt in place of the (necessarily empty) grids.
  // Skipped in mock/demo mode, which always has data.
  const { data: repositories } = useRepositories(useMock ? null : orgSlug);
  const noRepos = !useMock && !!orgSlug && repositories?.length === 0;
  const createPlan = useCreatePlan(orgSlug);
  const updatePlan = useUpdatePlan(orgSlug);
  const deletePlan = useDeletePlan(orgSlug);
  const createRoadmap = useCreateRoadmap(orgSlug);
  const updateRoadmap = useUpdateRoadmap(orgSlug);

  const [state, dispatch] = useReducer(
    roadmapReducer,
    view,
    (v) =>
      initialRoadmapState({ roadmaps: v.roadmaps, projects: v.projects })
  );
  // After a plan save, the just-saved id is parked here so the next
  // refetch (which carries the new/updated plan) pins it into the
  // selection — making the change visible in-session without a manual
  // refresh. Consumed (and cleared) by the reconcile effect below once
  // the refetched `view` actually contains the id.
  const pendingSelectRef = useRef<{
    kind: 'project' | 'team_change';
    id: string;
  } | null>(null);
  // The reducer's lazy initializer runs once at mount and never re-syncs
  // with refetched data. Fold each fresh `view` back in: prune selected
  // ids that no longer exist (e.g. a deleted plan) and pin any pending
  // just-saved id. `sync-view` is idempotent, so this firing on every
  // background refetch is a render-cheap no-op when nothing changed.
  useEffect(() => {
    const projectIds = new Set<string>([
      ...view.projects.map(p => p.id),
      ...view.plans.filter(p => p.kind === 'project').map(p => p.id),
    ]);
    const teamChangeIds = new Set<string>([
      ...view.team_changes.map(t => t.id),
      ...view.plans.filter(p => p.kind === 'team_change').map(p => p.id),
    ]);
    const pending = pendingSelectRef.current;
    let ensureProject: string | undefined;
    let ensureTeamChange: string | undefined;
    // Only honor the pin once the refetched view actually contains the
    // id, else the grid would resolve to an empty ProjectView for a
    // frame. If it's not landed yet, keep it pending for the next
    // refetch.
    if (pending?.kind === 'project' && projectIds.has(pending.id)) {
      ensureProject = pending.id;
      pendingSelectRef.current = null;
    } else if (
      pending?.kind === 'team_change' &&
      teamChangeIds.has(pending.id)
    ) {
      ensureTeamChange = pending.id;
      pendingSelectRef.current = null;
    }
    dispatch({
      type: 'sync-view',
      projectIds,
      teamChangeIds,
      ...(ensureProject ? { ensureProject } : {}),
      ...(ensureTeamChange ? { ensureTeamChange } : {}),
    });
  }, [view]);
  const [grouped, setGrouped] = useState(true);
  const [showLibraries, setShowLibraries] = useState(false);
  // When the scaffold was entered via the sidebar's pencil affordance
  // on an existing plan, this carries the plan's wire id so Save
  // dispatches PATCH (`updatePlan`) instead of POST (`createPlan`).
  // Reset to null whenever a scaffold exits (success or cancel).
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  // Confirmation dialog for the sidebar's trash affordance. `null`
  // hides the dialog; otherwise the payload describes what's about
  // to be deleted so the dialog can render the plan's display name
  // in the prompt.
  const [pendingDelete, setPendingDelete] = useState<{
    id: string;
    kind: 'project' | 'team_change';
    display: string;
  } | null>(null);
  // Linked-skill delete intercept: when the user clicks delete on a
  // skill whose owning comp is linked across other plans in the
  // active roadmap, we capture the affected plan display names + a
  // deferred local-state mutation here so a `ConfirmDialog` can list
  // the affected plans before the row is removed. The actual
  // cross-plan delete propagates on save via `propagateLinkedOverlay`.
  const [pendingLinkedSkillDelete, setPendingLinkedSkillDelete] = useState<{
    skillDisplay: string;
    compDisplay: string;
    affectedPlanDisplays: string[];
    onConfirm: () => void;
  } | null>(null);
  // Phase 1 scaffold: when true, the view shifts into a "Create Project"
  // mode — sidebar hidden, blank Resource grid, Save button in toolbar.
  // No real form / persistence yet; Save just exits the mode.
  const [createMode, setCreateMode] = useState(false);
  // V1 scaffold mirror of createMode for the "+ New team change" entry
  // point. Renders the live talent map (no project columns yet) under a
  // dedicated H1/H2. No edit affordances wired yet.
  const [teamChangeMode, setTeamChangeMode] = useState(false);
  // Team-change display name — editable via the H1 affordance in
  // teamChangeMode. Saved as `Plan.display` when persisting; resets
  // to the default on scaffold exit. Mirrors `createProjectName`.
  const [teamChangeName, setTeamChangeName] = useState('New team change');
  // Placeholder ICs and comps appended via the ghost column/row in the
  // team-change scaffold. Each entry is a visual stub for now — full
  // editing wires up in a follow-up.
  const [teamChangeAddedICs, setTeamChangeAddedICs] = useState<
    Array<{ id: string; display: string }>
  >([]);
  const [teamChangeAddedComps, setTeamChangeAddedComps] = useState<
    Array<{ id: string; display: string }>
  >([]);
  // Skills added per-comp via the hover "+" affordance. Mirrors the
  // shape used by create-project (`createAddedSkills`) so both flows
  // work the same — new comps seed an entry here with a single
  // placeholder skill, and the "+" button appends more.
  const [teamChangeAddedSkills, setTeamChangeAddedSkills] = useState<
    Record<string, Array<{ id: string; display: string }>>
  >({});
  // Per-(engineer, skill) rating overrides. Absent entry = follow the
  // baseline; presence = user has edited. Cells with an entry render
  // with the dashed "modified" outline.
  const [teamChangeRatingOverrides, setTeamChangeRatingOverrides] = useState<
    Record<string, Record<string, RatingValue>>
  >({});
  // Existing ICs (from the snapshot) the user has flagged as departed
  // for this team-change session — applied as a visual override only;
  // the underlying snapshot is untouched.
  const [teamChangeDepartedIDs, setTeamChangeDepartedIDs] = useState<
    Set<string>
  >(() => new Set());
  // Per-IC display renames (both added + existing). Session-scoped.
  const [teamChangeICRenames, setTeamChangeICRenames] = useState<
    Record<string, string>
  >({});
  // Per-(engineer, skill) function assignment overrides. Mirrors the
  // create-project `createSkillFunctions` shape — absent entry = use
  // the implicit default (all comp.functions intersecting the skill).
  const [teamChangeSkillFunctions, setTeamChangeSkillFunctions] = useState<
    Record<string, Record<string, string[]>>
  >({});
  // Profiles (roles) on the in-progress project. Each "+" ghost-column
  // click appends one. Reset whenever create mode is (re)entered.
  const [createRoles, setCreateRoles] = useState<
    Array<{ id: string; display: string }>
  >([{ id: 'role-1', display: 'Build' }]);
  // Placeholder derivedContexts appended via the bottom ghost "+" row.
  const [createAddedComps, setCreateAddedComps] = useState<
    Array<{ id: string; display: string }>
  >([]);
  // Skills added via the hover "+" affordance, keyed by comp.id.
  // Lives in a separate map (vs being embedded in the comp record) so
  // BOTH default-catalog comps and newly-added comps can grow skills.
  const [createAddedSkills, setCreateAddedSkills] = useState<
    Record<string, Array<{ id: string; display: string }>>
  >({});
  // Inline-edit overrides for derivedContext displays (default catalog +
  // newly added). Keyed by comp.id. Cleared whenever create mode is
  // (re)entered / exited.
  const [createCompOverrides, setCreateCompOverrides] = useState<
    Record<string, string>
  >({});
  // Per-(role, skill) required proficiency picked via the RequirementCell
  // popover in flat mode. Outer key = role.id, inner key = skill.id.
  const [createRequirements, setCreateRequirements] = useState<
    Record<string, Record<string, RatingValue>>
  >({});
  // Parallel map for cells marked "Deprecated". Stored separately so the
  // existing { level: RatingValue } requirementsByRole shape is untouched
  // — the renderer overlays the two when deciding the cell's visual.
  const [createDeprecated, setCreateDeprecated] = useState<
    Record<string, Record<string, true>>
  >({});
  // Project name as shown in the banner; editable via the same inline
  // label component used for roles + new derivedContexts.
  const [createProjectName, setCreateProjectName] = useState('New project');
  // Explicit per-(role, skill) function assignments. ABSENT entry means
  // "use the default" — all comp.functions intersecting the skill, each
  // implicitly at impact area 1. The state only stores the *override*
  // after the user has touched the function picker for that cell.
  const [createSkillFunctions, setCreateSkillFunctions] = useState<
    Record<string, Record<string, string[]>>
  >({});

  // Combine legacy Project-derived views (mock data + back-compat) with
  // saved project-kind Plans. The grid components (Resource / Impact /
  // Diff) consume the `ProjectView` shape uniformly — the sidebar's
  // `select-project` dispatch sets `state.projectId`, `activeProject`
  // resolves against this map, and the corresponding overlay renders.
  const projectViews = useMemo(() => {
    const fromProjects = buildProjectViews(view);
    const fromPlans = buildPlanProjectViews(view);
    // Plans win on slug collision — saved scaffolds are the
    // authored truth post-PR-#140; mock projects are only there for
    // the marketing preview path.
    for (const [id, pv] of fromPlans) fromProjects.set(id, pv);
    return fromProjects;
  }, [view]);
  // Combine legacy TeamChange-derived rosters (mock data + back-compat)
  // with saved team-change-kind Plans. The projection pipeline
  // (`projectionContext`) consumes this Map uniformly — the sidebar's
  // `select-team-change` dispatch sets `state.teamChangeId`, the map
  // resolves a roster, and Resource / Diff render with the overlay.
  const projectedRosters = useMemo(() => {
    const fromTeamChanges = buildProjectedRosters(view);
    const fromPlans = buildPlanProjectedRosters({
      plans: view.plans,
      derivedContexts: view.derivedContexts,
      engineers: view.engineers,
      ratings: view.ratings,
    });
    // Plans win on slug collision — saved scaffolds are the authored
    // truth post-PR-#140; mock team_changes are only there for the
    // marketing preview path.
    for (const [id, roster] of fromPlans) fromTeamChanges.set(id, roster);
    return fromTeamChanges;
  }, [view]);
  const liveRatingIndex = useMemo(
    () =>
      buildRatingIndex(
        indexSkills(view.derivedContexts),
        view.engineers,
        view.ratings
      ),
    [view.derivedContexts, view.engineers, view.ratings]
  );

  const activeProject = useMemo(
    () => resolveActiveProject(view, state, projectViews),
    [view, state, projectViews]
  );

  // Resource view operates on a separate synthetic ProjectView built
  // from the full talent catalog (not just demand-bearing skills).
  const resourceProject = useMemo(
    () =>
      talentResourceProjection({
        state,
        derivedContexts: view.derivedContexts,
        roadmaps: view.roadmaps,
        projectViews,
        showLibraries,
      }),
    [state, view.derivedContexts, view.roadmaps, projectViews, showLibraries]
  );

  // Team-change-mode scaffold: just the live talent map (no project
  // toggles, no projects pinned). Same `talentResourceProjection` the
  // regular Resource view uses with no selection — surfaces engineer /
  // agent / departed columns against the full skill catalog. Added
  // comps from the ghost row are appended as overlay rows so they
  // render with the standard "NEW" blue + bold treatment.
  const teamChangeProject = useMemo(() => {
    if (!teamChangeMode) return null;
    // Entering "+ New team change" should ALWAYS start from the bare
    // live talent map — no roadmap, no project selection, no
    // team-change overlay carried over from the regular Roadmap view.
    // Pre-PR-#153 this cleared `projectId` / `projectToggles` /
    // `teamChangeToggles`; the new state model needs
    // `selectedProjectIds` / `selectedTeamChangeId` cleared instead so
    // `talentResourceProjection` doesn't overlay the user's current
    // project selection on top of the team-change scaffold.
    const blankState = {
      ...state,
      roadmapId: null,
      selectedProjectIds: [],
      selectedTeamChangeId: null,
    };
    const base = talentResourceProjection({
      state: blankState,
      derivedContexts: view.derivedContexts,
      roadmaps: view.roadmaps,
      projectViews,
      showLibraries,
    });
    if (!base) return base;
    const wrapAddedSkill = (s: { id: string; display: string }) => ({
      id: s.id,
      display: s.display,
      kind: 'framework' as const,
      institutional: false,
      is_overlay: true,
    });
    const addedMergedComps = teamChangeAddedComps.map(c => ({
      object: 'derived_context' as const,
      id: c.id,
      display: c.display,
      subtitle: null,
      functions: [] as string[],
      created_at: 0,
      updated_at: 0,
      is_overlay: true,
      skills: (teamChangeAddedSkills[c.id] ?? []).map(wrapAddedSkill),
    }));
    const augmentedDefault = base.mergedDerivedContexts.map(c => {
      const extras = teamChangeAddedSkills[c.id] ?? [];
      if (extras.length === 0) return c;
      return { ...c, skills: [...c.skills, ...extras.map(wrapAddedSkill)] };
    });
    return {
      ...base,
      mergedDerivedContexts: [...augmentedDefault, ...addedMergedComps],
    };
  }, [
    teamChangeMode,
    teamChangeAddedComps,
    teamChangeAddedSkills,
    state,
    view.derivedContexts,
    view.roadmaps,
    projectViews,
    showLibraries,
  ]);

  // Skills marked deprecated for every role drive the strikethrough
  // treatment on label rows + the "restore" affordance. A skill is
  // "fully deprecated" iff every existing role flags it deprecated;
  // partial deprecation (some roles) keeps the row live.
  const deprecatedSkillIds = useMemo(() => {
    if (!createMode || createRoles.length === 0) return new Set<string>();
    const all = new Set<string>();
    for (const roleMap of Object.values(createDeprecated)) {
      for (const skillId of Object.keys(roleMap)) all.add(skillId);
    }
    const out = new Set<string>();
    for (const skillId of all) {
      const everywhere = createRoles.every(
        r => createDeprecated[r.id]?.[skillId] === true
      );
      if (everywhere) out.add(skillId);
    }
    return out;
  }, [createMode, createRoles, createDeprecated]);

  // Roadmap-draft overlay pool. Surfaces overlay comps + skills added
  // in OTHER plans referenced by this roadmap session as picker
  // suggestions, so a new project / team-change can reuse the same
  // Mobile comp (and its skill cluster) introduced by Plan A instead
  // of duplicating it. Anything already in the open scaffold is
  // excluded so the picker never suggests re-adding what's on screen.
  // Scoped to currently-selected plans + active scaffold — separate
  // roadmaps start with a fresh pool.
  const overlayPool = useMemo(() => {
    const compById = new Map<string, OverlayOption>();
    const skillsByComp = new Map<string, Map<string, OverlayOption>>();
    const ingest = (planId: string | null | undefined) => {
      if (!planId) return;
      const plan = view.plans.find(p => p.id === planId);
      if (!plan) return;
      for (const c of plan.payload.added_derived_contexts) compById.set(c.id, c);
      for (const [compId, skillList] of Object.entries(
        plan.payload.added_skills,
      )) {
        let m = skillsByComp.get(compId);
        if (!m) {
          m = new Map();
          skillsByComp.set(compId, m);
        }
        for (const s of skillList) m.set(s.id, s);
      }
    };
    for (const id of state.selectedProjectIds) ingest(id);
    ingest(state.selectedTeamChangeId);
    // Drop anything already added by the open scaffold — those aren't
    // candidates for re-adding.
    const dropFromActive = (
      comps: Array<{ id: string }>,
      skills: Record<string, Array<{ id: string }>>,
    ) => {
      for (const c of comps) compById.delete(c.id);
      for (const [compId, skillList] of Object.entries(skills)) {
        const m = skillsByComp.get(compId);
        if (!m) continue;
        for (const s of skillList) m.delete(s.id);
      }
    };
    if (createMode) dropFromActive(createAddedComps, createAddedSkills);
    if (teamChangeMode)
      dropFromActive(teamChangeAddedComps, teamChangeAddedSkills);

    const compsPool: OverlayOption[] = Array.from(compById.values());
    const skillsPool: Record<string, OverlayOption[]> = {};
    for (const [compId, m] of skillsByComp) {
      skillsPool[compId] = Array.from(m.values());
    }
    return { compsPool, skillsPool };
  }, [
    view.plans,
    state.selectedProjectIds,
    state.selectedTeamChangeId,
    createMode,
    createAddedComps,
    createAddedSkills,
    teamChangeMode,
    teamChangeAddedComps,
    teamChangeAddedSkills,
  ]);

  // Linked-overlay references: per (overlay comp / skill) id, how many
  // OTHER plans in this roadmap session also reference it. Drives the
  // warning indicator on EditableLabel ("editing here updates N linked
  // plans") and is sourced the same way as the pool — from currently-
  // selected plans, minus the plan currently open in a scaffold.
  const linkedRefs = useMemo(() => {
    const compCounts = new Map<string, number>();
    // Skills are scoped to OVERLAY-comp ownership: only skills under
    // an overlay comp propagate across plans (catalog-comp additions
    // stay per-plan). Tracking skills under catalog comps here would
    // mis-badge them as linked.
    const skillCounts = new Map<string, Map<string, number>>();
    const ingest = (planId: string | null | undefined) => {
      if (!planId) return;
      // Exclude the plan currently being edited so renames don't
      // self-warn.
      if (editingPlanId && planId === editingPlanId) return;
      const plan = view.plans.find(p => p.id === planId);
      if (!plan) return;
      const overlayCompIds = new Set(
        plan.payload.added_derived_contexts.map(c => c.id),
      );
      for (const c of plan.payload.added_derived_contexts) {
        compCounts.set(c.id, (compCounts.get(c.id) ?? 0) + 1);
      }
      for (const [compId, skillList] of Object.entries(
        plan.payload.added_skills,
      )) {
        if (!overlayCompIds.has(compId)) continue;
        let m = skillCounts.get(compId);
        if (!m) {
          m = new Map();
          skillCounts.set(compId, m);
        }
        for (const s of skillList) {
          m.set(s.id, (m.get(s.id) ?? 0) + 1);
        }
      }
    };
    for (const id of state.selectedProjectIds) ingest(id);
    ingest(state.selectedTeamChangeId);
    return { compCounts, skillCounts };
  }, [
    view.plans,
    state.selectedProjectIds,
    state.selectedTeamChangeId,
    editingPlanId,
  ]);

  // Create-mode scaffold: a blank project Resource view — full talent
  // catalog + a single empty "Build" role column, no team columns. We
  // reuse `talentResourceProjection` with a cleared selection so the
  // catalog assembly logic stays in one place, then inject the
  // placeholder role group on top.
  const createProject = useMemo(() => {
    if (!createMode) return null;
    // Same rationale as `teamChangeProject` — the create-project
    // scaffold builds against a bare base view, then layers the
    // scaffold's own roles + added comps/skills on top.
    const blankState = {
      ...state,
      roadmapId: null,
      selectedProjectIds: [],
      selectedTeamChangeId: null,
    };
    const base = talentResourceProjection({
      state: blankState,
      derivedContexts: view.derivedContexts,
      roadmaps: view.roadmaps,
      projectViews,
      showLibraries,
    });
    if (!base) return base;
    const wrapAddedSkill = (s: { id: string; display: string }) => ({
      id: s.id,
      display: s.display,
      kind: 'framework' as const,
      is_overlay: true,
    });
    // Newly-added comps: skills come entirely from the added-skills map.
    const addedMergedComps = createAddedComps.map(c => ({
      object: 'derived_context' as const,
      id: c.id,
      display: c.display,
      subtitle: null,
      functions: [] as string[],
      created_at: 0,
      updated_at: 0,
      is_overlay: true,
      skills: (createAddedSkills[c.id] ?? []).map(wrapAddedSkill),
    }));
    // Default-catalog comps: append any skills the user added against
    // them this session (each tagged `is_overlay` so labels render with
    // the blue/bold "new" treatment).
    const augmentedDefault = base.mergedDerivedContexts.map(c => {
      const extras = createAddedSkills[c.id] ?? [];
      if (extras.length === 0) return c;
      return { ...c, skills: [...c.skills, ...extras.map(wrapAddedSkill)] };
    });
    // Apply inline-edit overrides so renamed derivedContexts render with
    // their new display in the grid. Keeps the snapshot data untouched.
    const allComps = [...augmentedDefault, ...addedMergedComps].map(
      c => ({ ...c, display: createCompOverrides[c.id] ?? c.display })
    );
    return {
      ...base,
      display: 'New project',
      mergedDerivedContexts: allComps,
      projectGroups: [
        {
          id: '__new-project__',
          display: createProjectName,
          roles: createRoles,
          requirementsByRole: Object.fromEntries(
            createRoles.map(r => [
              r.id,
              Object.fromEntries(
                Object.entries(createRequirements[r.id] ?? {}).map(
                  ([skillId, level]) => [skillId, { level }]
                )
              ),
            ])
          ),
        },
      ],
    };
  }, [
    createMode,
    createRoles,
    createAddedComps,
    createAddedSkills,
    createCompOverrides,
    createRequirements,
    createProjectName,
    state,
    view.derivedContexts,
    view.roadmaps,
    projectViews,
    showLibraries,
  ]);

  const ctx = useMemo(
    () =>
      projectionContext({
        state,
        engineers: view.engineers,
        roadmaps: view.roadmaps,
        projectedRosters,
        liveRatingIndex,
      }),
    [state, view.engineers, view.roadmaps, projectedRosters, liveRatingIndex]
  );

  // Layer the active team-change plan's skill-axis overlay onto the
  // Resource view's ProjectView. `ctx.teamChangeId` resolves to the
  // currently-applied team change (roadmap-scoped or standalone) — we
  // look that plan up from `view.plans` and merge its
  // `added_derived_contexts` / `added_skills` / `comp_overrides` onto
  // `resourceProject.mergedDerivedContexts` so new comps appear as rows
  // and added skills hang off the right comp. Engineer-axis changes
  // already applied via `projectionContext`; this finishes the loop on
  // the row axis.
  //
  // Legacy mock `view.team_changes` aren't plans and carry no overlay
  // payload, so they pass through unchanged.
  const resourceProjectWithOverlay = useMemo(() => {
    if (!resourceProject) return resourceProject;
    if (!ctx.teamChangeId) return resourceProject;
    const plan = view.plans.find((p) => p.id === ctx.teamChangeId);
    // Discriminated-union narrowing: the find predicate doesn't tighten
    // `plan.payload`'s union, so re-check `kind` here.
    if (!plan || plan.kind !== 'team_change') return resourceProject;
    return applyPlanCompOverlay(resourceProject, {
      added_derived_contexts: plan.payload.added_derived_contexts,
      added_skills: plan.payload.added_skills,
      // Team-change scaffold has no comp-rename affordance; only the
      // project scaffold captures `comp_overrides`.
      comp_overrides: {},
    });
  }, [resourceProject, ctx.teamChangeId, view.plans]);

  // Baseline projection context with ALL toggles cleared — surfaces the
  // snapshot's live team + ratings with no roadmap / project / team-
  // change scenario applied. Used as the starting point for the New
  // Team Change view so opening it always shows the live talent map,
  // regardless of what was toggled on in the regular Roadmap view.
  const liveCtx = useMemo(() => {
    // Baseline projection context = no scenario applied. Clear the
    // team-change selection so `projectionContext` doesn't apply an
    // overlay roster on top of the team-change scaffold's "live
    // talent map" starting point.
    const blankState = {
      ...state,
      roadmapId: null,
      selectedProjectIds: [],
      selectedTeamChangeId: null,
    };
    return projectionContext({
      state: blankState,
      engineers: view.engineers,
      roadmaps: view.roadmaps,
      projectedRosters,
      liveRatingIndex,
    });
  }, [state, view.engineers, view.roadmaps, projectedRosters, liveRatingIndex]);

  // Layer the added-IC stubs into the projection context so the grid
  // renders them as additional human columns with `is_new: true` (which
  // already maps to the blue / bold "new hire" header styling). Also
  // applies session renames to every team column so the new display
  // ripples through grouped/flat headers + tooltips. Built on top of
  // `liveCtx` so the team-change scaffold ignores any existing scenario
  // toggled in the regular Roadmap view.
  const teamChangeCtx = useMemo(() => {
    if (!teamChangeMode) return ctx;
    const base = liveCtx;
    const extraHumans: ProjectedTalentColumn[] = teamChangeAddedICs.map(ic => ({
      id: ic.id,
      display_name: teamChangeICRenames[ic.id] ?? ic.display,
      github_login: null,
      kind: 'human' as const,
      is_new: true,
    }));
    const renamedTeam = base.teamColumns.map(c => {
      const rename = teamChangeICRenames[c.id];
      return rename ? { ...c, display_name: rename } : c;
    });
    if (extraHumans.length === 0 && renamedTeam === base.teamColumns) {
      return base;
    }
    return { ...base, teamColumns: [...renamedTeam, ...extraHumans] };
  }, [teamChangeMode, ctx, liveCtx, teamChangeAddedICs, teamChangeICRenames]);

  // Hoisted out of the JSX so the Impact / Diff empty-state CTA can
  // call into the same scaffold-entry path the sidebar's
  // "+ New project" / "+ New team change" buttons use.
  function enterCreateProjectMode() {
    setCreateRoles([{ id: 'role-1', display: 'Build' }]);
    setCreateAddedComps([]);
    setCreateCompOverrides({});
    setCreateRequirements({});
    setCreateDeprecated({});
    setCreateSkillFunctions({});
    setCreateProjectName('New project');
    setCreateMode(true);
  }
  function enterTeamChangeMode() {
    setTeamChangeAddedICs([]);
    setTeamChangeAddedComps([]);
    setTeamChangeAddedSkills({});
    setTeamChangeRatingOverrides({});
    setTeamChangeDepartedIDs(new Set());
    setTeamChangeICRenames({});
    setTeamChangeSkillFunctions({});
    setTeamChangeName('New team change');
    setTeamChangeMode(true);
  }

  // Exit-scaffold helpers, factored out so Save success, Cancel, and
  // post-delete cleanup all leave the scaffold in the same idle
  // state. `setEditingPlanId(null)` ensures that the next Save
  // dispatches POST (not PATCH).
  function resetCreateScaffold() {
    setCreateMode(false);
    setEditingPlanId(null);
    setCreateRoles([{ id: 'role-1', display: 'Build' }]);
    setCreateAddedComps([]);
    setCreateAddedSkills({});
    setCreateCompOverrides({});
    setCreateRequirements({});
    setCreateDeprecated({});
    setCreateSkillFunctions({});
    setCreateProjectName('New project');
  }
  function resetTeamChangeScaffold() {
    setTeamChangeMode(false);
    setEditingPlanId(null);
    setTeamChangeAddedICs([]);
    setTeamChangeAddedComps([]);
    setTeamChangeAddedSkills({});
    setTeamChangeRatingOverrides({});
    setTeamChangeDepartedIDs(new Set());
    setTeamChangeICRenames({});
    setTeamChangeSkillFunctions({});
    setTeamChangeName('New team change');
  }

  // Wire data: the sidebar's selection holds prefixed wire ids
  // (`plan_<slug>` for project + team-change references); backend
  // expects raw slugs. Strip on the wire.
  function unwrapPlanId(prefixedId: string): string {
    return parsePublicId(IdPrefix.plan, prefixedId);
  }

  function handleCreateRoadmap(display: string) {
    if (createRoadmap.isPending) return;
    const slug = scaffoldSlug(display);
    createRoadmap.mutate({
      id: slug,
      display,
      projects: state.selectedProjectIds.map(unwrapPlanId),
      team_change: state.selectedTeamChangeId
        ? unwrapPlanId(state.selectedTeamChangeId)
        : null,
    });
  }

  function handleUpdateRoadmap() {
    if (updateRoadmap.isPending) return;
    if (!state.roadmapId) return;
    // PATCH /v1/orgs/:slug/roadmaps/:id — the route handler runs
    // `parsePublicId(RMAP, c.req.param('id'))` on the path param, so
    // we send the prefixed wire id (`rmap_<slug>`) as-is. Body
    // fields (projects + team_change) carry RAW slugs though, so
    // those still get unwrapped from their `plan_*` wire form.
    updateRoadmap.mutate({
      id: state.roadmapId,
      body: {
        projects: state.selectedProjectIds.map(unwrapPlanId),
        team_change: state.selectedTeamChangeId
          ? unwrapPlanId(state.selectedTeamChangeId)
          : null,
      },
    });
  }

  /**
   * After a scaffold save, fan out to every OTHER plan in the
   * roadmap that links to one of the just-saved comp/skill ids and
   * PATCH it with the rename or skill-add the user just made. Each
   * target's `requirements`, `ratings`, `function_assignments`, and
   * other state stay untouched — only `added_derived_contexts` /
   * `added_skills` get re-aligned. Errors are swallowed so a single
   * failed PATCH doesn't block the scaffold from resetting; the
   * roadmap query gets invalidated by useUpdatePlan regardless, so
   * the next refetch picks up whatever did succeed.
   */
  async function propagateLinkedOverlay(
    source: {
      added_derived_contexts: Array<{ id: string; display: string }>;
      added_skills: Record<string, Array<{ id: string; display: string }>>;
    },
    sourcePlanId: string | null,
  ) {
    const patches: Array<Promise<unknown>> = [];
    for (const other of view.plans) {
      if (sourcePlanId && other.id === sourcePlanId) continue;
      const patch = computeLinkedPatch(source, other.payload);
      if (!patch) continue;
      const fullPayload =
        other.kind === 'project'
          ? applyPatchToProjectPayload(other.payload, patch)
          : applyPatchToTeamChangePayload(other.payload, patch);
      patches.push(
        updatePlan
          .mutateAsync({ id: other.id, body: { payload: fullPayload } })
          .catch(() => undefined),
      );
    }
    if (patches.length > 0) await Promise.all(patches);
  }

  /**
   * Intercept for "delete a skill under a linked overlay comp". When
   * the skill is genuinely linked (its owning comp lives in some
   * OTHER plan in the active roadmap), park the delete in
   * `pendingLinkedSkillDelete` so a ConfirmDialog can list every
   * affected plan before mutation. When it's NOT linked, the helper
   * fires `performDelete` synchronously and short-circuits the modal.
   */
  function maybeInterceptLinkedSkillDelete(args: {
    compId: string;
    skillId: string;
    compDisplay: string;
    skillDisplay: string;
    performDelete: () => void;
  }) {
    const skillCount =
      linkedRefs.skillCounts.get(args.compId)?.get(args.skillId) ?? 0;
    if (skillCount === 0) {
      args.performDelete();
      return;
    }
    const affected: string[] = [];
    for (const id of state.selectedProjectIds) {
      if (editingPlanId && id === editingPlanId) continue;
      const plan = view.plans.find(p => p.id === id);
      if (!plan) continue;
      const linksComp = plan.payload.added_derived_contexts.some(
        c => c.id === args.compId,
      );
      const hasSkill = (plan.payload.added_skills[args.compId] ?? []).some(
        s => s.id === args.skillId,
      );
      if (linksComp && hasSkill) affected.push(plan.display);
    }
    if (state.selectedTeamChangeId) {
      const plan = view.plans.find(
        p => p.id === state.selectedTeamChangeId,
      );
      if (plan && (!editingPlanId || plan.id !== editingPlanId)) {
        const linksComp = plan.payload.added_derived_contexts.some(
          c => c.id === args.compId,
        );
        const hasSkill = (
          plan.payload.added_skills[args.compId] ?? []
        ).some(s => s.id === args.skillId);
        if (linksComp && hasSkill) affected.push(plan.display);
      }
    }
    setPendingLinkedSkillDelete({
      skillDisplay: args.skillDisplay,
      compDisplay: args.compDisplay,
      affectedPlanDisplays: affected,
      onConfirm: args.performDelete,
    });
  }

  const isSavingRoadmap = createRoadmap.isPending || updateRoadmap.isPending;

  // ----- Edit / delete plan affordances -----

  function handleEditPlan(planId: string, kind: 'project' | 'team_change') {
    const plan = view.plans.find((p) => p.id === planId);
    if (!plan) return;
    if (kind === 'project' && plan.kind === 'project') {
      // Hydrate every project-scaffold state slot from payload so the
      // scaffold reopens in the exact configuration the user saved.
      // Roles default to a single Build profile if the saved plan
      // didn't capture any (defensive — current scaffold always
      // writes at least one).
      setCreateRoles(
        plan.payload.roles.length > 0
          ? plan.payload.roles
          : [{ id: 'role-1', display: 'Build' }],
      );
      setCreateAddedComps(plan.payload.added_derived_contexts);
      setCreateAddedSkills(plan.payload.added_skills);
      setCreateCompOverrides(plan.payload.comp_overrides);
      // Upgrade legacy (skill-only) payloads to (comp::skill)-scoped maps so
      // a re-opened older plan edits per-derivedContext like a fresh one. New
      // plans already carry composite keys and pass through unchanged.
      const skillToComps = buildSkillToComps([
        ...view.derivedContexts.map(c => ({
          id: c.id,
          skills: [...c.skills, ...(plan.payload.added_skills[c.id] ?? [])],
        })),
        ...plan.payload.added_derived_contexts.map(ac => ({
          id: ac.id,
          skills: plan.payload.added_skills[ac.id] ?? [],
        })),
      ]);
      setCreateRequirements(
        normalizeLegacyReqMap(plan.payload.requirements, skillToComps),
      );
      setCreateDeprecated(
        normalizeLegacyReqMap(plan.payload.deprecated, skillToComps),
      );
      setCreateSkillFunctions(
        normalizeLegacyReqMap(plan.payload.function_assignments, skillToComps),
      );
      setCreateProjectName(plan.display);
      setEditingPlanId(plan.id);
      setCreateMode(true);
    } else if (kind === 'team_change' && plan.kind === 'team_change') {
      setTeamChangeAddedICs(plan.payload.added_ics);
      setTeamChangeAddedComps(plan.payload.added_derived_contexts);
      setTeamChangeAddedSkills(plan.payload.added_skills);
      setTeamChangeRatingOverrides(plan.payload.ratings);
      setTeamChangeDepartedIDs(new Set(plan.payload.departed_ids));
      setTeamChangeICRenames(plan.payload.ic_renames);
      setTeamChangeSkillFunctions(plan.payload.function_assignments);
      setTeamChangeName(plan.display);
      setEditingPlanId(plan.id);
      setTeamChangeMode(true);
    }
  }

  function handleDeletePlan(
    planId: string,
    kind: 'project' | 'team_change',
    display: string,
  ) {
    setPendingDelete({ id: planId, kind, display });
  }

  function confirmDelete() {
    if (!pendingDelete) return;
    if (deletePlan.isPending) return;
    const { id, kind } = pendingDelete;
    deletePlan.mutate(id, {
      onSuccess: () => {
        // Clear the deleted slug from the active selection so the
        // sidebar's CheckboxRow / RadioRow doesn't keep a stale
        // reference once the refetch lands.
        if (kind === 'project') {
          dispatchClearProject(id);
        } else if (state.selectedTeamChangeId === id) {
          dispatch({ type: 'select-team-change', teamChangeId: null });
        }
        // If the deleted plan was open in the scaffold (toolbar Delete
        // path), also exit the scaffold — there's nothing left to
        // edit. resetXScaffold also clears `editingPlanId` so the
        // next entry into either scaffold dispatches POST (not PATCH).
        if (editingPlanId === id) {
          if (kind === 'project') {
            resetCreateScaffold();
          } else {
            resetTeamChangeScaffold();
          }
        }
        setPendingDelete(null);
      },
    });
  }

  function dispatchClearProject(id: string) {
    if (state.selectedProjectIds.includes(id)) {
      dispatch({ type: 'toggle-project', projectId: id });
    }
  }

  return (
    <section>
      {createMode ? (
        <>
          <EditableH1 value={createProjectName} onChange={setCreateProjectName} />
          <h2 className='snap-subtitle'>
            {editingPlanId ? 'Edit project' : 'Create project'}
          </h2>
        </>
      ) : teamChangeMode ? (
        <>
          <EditableH1 value={teamChangeName} onChange={setTeamChangeName} />
          <h2 className='snap-subtitle'>
            {editingPlanId ? 'Edit team change' : 'Model new changes to your team'}
          </h2>
        </>
      ) : (
        <>
          <h1 className='snap-h1'>Roadmap</h1>
          <p className='snap-subtitle'>Project planning &amp; feasibility.</p>
        </>
      )}

      <FloatingToolbar>
        {!createMode && !teamChangeMode ? (
          <ToolbarSegmented
            label='View'
            value={state.view}
            options={[
              { value: 'resource', label: 'Resource' },
              { value: 'impact', label: 'Impact' },
              { value: 'diff', label: 'Diff' },
            ]}
            onChange={view => dispatch({ type: 'set-view', view })}
          />
        ) : null}
        <ToolbarToggle
          label='Group Skills'
          pressed={grouped}
          onToggle={() => setGrouped(v => !v)}
        >
          <GroupSkillsIcon />
        </ToolbarToggle>
        <ToolbarToggle
          label='Show Libraries'
          pressed={showLibraries}
          onToggle={() => setShowLibraries(v => !v)}
        >
          <ShowLibrariesIcon />
        </ToolbarToggle>
        {createMode ? (
          <>
            <ToolbarButton
              label='Cancel'
              variant='ghost'
              disabled={createPlan.isPending || updatePlan.isPending}
              onClick={resetCreateScaffold}
            />
            {editingPlanId ? (
              <ToolbarButton
                label='Delete'
                variant='danger'
                disabled={createPlan.isPending || updatePlan.isPending}
                onClick={() =>
                  setPendingDelete({
                    id: editingPlanId,
                    kind: 'project',
                    display: createProjectName,
                  })
                }
              />
            ) : null}
            <ToolbarButton
              label={
                createPlan.isPending || updatePlan.isPending
                  ? editingPlanId
                    ? 'Updating…'
                    : 'Saving…'
                  : editingPlanId
                    ? 'Update'
                    : 'Save'
              }
              onClick={async () => {
                if (createPlan.isPending || updatePlan.isPending) return;
                const payload = {
                  roles: createRoles,
                  added_derived_contexts: createAddedComps,
                  added_skills: createAddedSkills,
                  comp_overrides: createCompOverrides,
                  requirements: createRequirements,
                  deprecated: createDeprecated,
                  function_assignments: createSkillFunctions,
                };
                try {
                  let savedId: string;
                  if (editingPlanId) {
                    await updatePlan.mutateAsync({
                      id: editingPlanId,
                      body: { display: createProjectName, payload },
                    });
                    savedId = editingPlanId;
                  } else {
                    const created = await createPlan.mutateAsync({
                      id: scaffoldSlug(createProjectName),
                      display: createProjectName,
                      kind: 'project',
                      payload,
                    });
                    savedId = created.id;
                  }
                  // Pin the saved project so the reconcile effect renders
                  // it once the refetch lands — no manual refresh needed.
                  pendingSelectRef.current = { kind: 'project', id: savedId };
                  await propagateLinkedOverlay(payload, editingPlanId);
                  resetCreateScaffold();
                } catch {
                  // Mutation hook surfaces error state; keep the
                  // scaffold open so the user can retry.
                }
              }}
            />
          </>
        ) : teamChangeMode ? (
          <>
            <ToolbarButton
              label='Cancel'
              variant='ghost'
              disabled={createPlan.isPending || updatePlan.isPending}
              onClick={resetTeamChangeScaffold}
            />
            {editingPlanId ? (
              <ToolbarButton
                label='Delete'
                variant='danger'
                disabled={createPlan.isPending || updatePlan.isPending}
                onClick={() =>
                  setPendingDelete({
                    id: editingPlanId,
                    kind: 'team_change',
                    display: teamChangeName,
                  })
                }
              />
            ) : null}
            <ToolbarButton
              label={
                createPlan.isPending || updatePlan.isPending
                  ? editingPlanId
                    ? 'Updating…'
                    : 'Saving…'
                  : editingPlanId
                    ? 'Update'
                    : 'Save'
              }
              onClick={async () => {
                if (createPlan.isPending || updatePlan.isPending) return;
                const payload = {
                  added_ics: teamChangeAddedICs,
                  added_derived_contexts: teamChangeAddedComps,
                  added_skills: teamChangeAddedSkills,
                  ratings: teamChangeRatingOverrides,
                  departed_ids: Array.from(teamChangeDepartedIDs),
                  ic_renames: teamChangeICRenames,
                  function_assignments: teamChangeSkillFunctions,
                };
                try {
                  let savedId: string;
                  if (editingPlanId) {
                    await updatePlan.mutateAsync({
                      id: editingPlanId,
                      body: { display: teamChangeName, payload },
                    });
                    savedId = editingPlanId;
                  } else {
                    const created = await createPlan.mutateAsync({
                      id: scaffoldSlug(teamChangeName),
                      display: teamChangeName,
                      kind: 'team_change',
                      payload,
                    });
                    savedId = created.id;
                  }
                  // Pin the saved team-change so the reconcile effect
                  // layers it once the refetch lands — no manual refresh.
                  pendingSelectRef.current = {
                    kind: 'team_change',
                    id: savedId,
                  };
                  await propagateLinkedOverlay(payload, editingPlanId);
                  resetTeamChangeScaffold();
                } catch {
                  // Mutation hook surfaces error state; keep the
                  // scaffold open so the user can retry.
                }
              }}
            />
          </>
        ) : null}
      </FloatingToolbar>

      {teamChangeMode ? (
        <div className='snap-roadmap snap-roadmap--team-change'>
          <div className='snap-roadmap__content'>
            <RoadmapResourceGrid
              project={teamChangeProject}
              ctx={teamChangeCtx}
              functionsCount={view.business_functions.length}
              grouped={grouped}
              teamChangeMode
              onAddIC={() =>
                setTeamChangeAddedICs(prev => [
                  ...prev,
                  {
                    id: `__new-ic-${prev.length + 1}__`,
                    display: 'New IC',
                  },
                ])
              }
              overlayCompsPool={overlayPool.compsPool}
              overlaySkillsPool={overlayPool.skillsPool}
              linkedCompCounts={linkedRefs.compCounts}
              linkedSkillCounts={linkedRefs.skillCounts}
              onAddDerivedContext={item => {
                // Picker hands back EITHER an existing pool entry (id
                // set — LINK to it) OR a free-text creation (id
                // undefined — MINT new). For links we also auto-pull
                // any pool skills under that comp so the row arrives
                // populated; for net-new we seed a placeholder skill
                // so visibleComps doesn't filter the empty row out.
                if (item.id) {
                  if (teamChangeAddedComps.some(c => c.id === item.id)) return;
                  const compId = item.id;
                  setTeamChangeAddedComps(prev => [
                    ...prev,
                    { id: compId, display: item.display },
                  ]);
                  const linkedSkills =
                    overlayPool.skillsPool[compId] ?? [];
                  setTeamChangeAddedSkills(prev => ({
                    ...prev,
                    [compId]:
                      linkedSkills.length > 0
                        ? linkedSkills.map(s => ({
                            id: s.id,
                            display: s.display,
                          }))
                        : [
                            {
                              id: `${compId}__skill-1`,
                              display: 'New skill',
                            },
                          ],
                  }));
                } else {
                  const compId = `__new-tc-comp-${Date.now()}__`;
                  setTeamChangeAddedComps(prev => [
                    ...prev,
                    { id: compId, display: item.display },
                  ]);
                  setTeamChangeAddedSkills(prev => ({
                    ...prev,
                    [compId]: [
                      { id: `${compId}__skill-1`, display: 'New skill' },
                    ],
                  }));
                }
              }}
              onAddSkill={(compId, item) => {
                // Link if the picker handed back an existing id, mint
                // otherwise. Skill-rows start blank (no override) — IC
                // ratings are set per-cell. Auto-ungroups so the new
                // row is immediately visible.
                const skillId =
                  item.id ?? `__new-tc-skill-${compId}-${Date.now()}`;
                if (
                  (teamChangeAddedSkills[compId] ?? []).some(
                    s => s.id === skillId,
                  )
                )
                  return;
                setTeamChangeAddedSkills(prev => ({
                  ...prev,
                  [compId]: [
                    ...(prev[compId] ?? []),
                    { id: skillId, display: item.display },
                  ],
                }));
                setGrouped(false);
              }}
              onRenameSkill={(compId, skillId, nextName) =>
                setTeamChangeAddedSkills(prev => ({
                  ...prev,
                  [compId]: (prev[compId] ?? []).map(s =>
                    s.id === skillId ? { ...s, display: nextName } : s
                  ),
                }))
              }
              onDeleteSkill={(compId, skillId) => {
                const compDisplay =
                  teamChangeAddedComps.find(c => c.id === compId)?.display ??
                  '';
                const skillDisplay =
                  (teamChangeAddedSkills[compId] ?? []).find(
                    s => s.id === skillId,
                  )?.display ?? '';
                const performDelete = () => {
                  setTeamChangeAddedSkills(prev => ({
                    ...prev,
                    [compId]: (prev[compId] ?? []).filter(
                      s => s.id !== skillId,
                    ),
                  }));
                  setTeamChangeRatingOverrides(prev => {
                    const next: typeof prev = {};
                    for (const [engId, engMap] of Object.entries(prev)) {
                      const filtered = { ...engMap };
                      delete filtered[skillId];
                      next[engId] = filtered;
                    }
                    return next;
                  });
                };
                maybeInterceptLinkedSkillDelete({
                  compId,
                  skillId,
                  compDisplay,
                  skillDisplay,
                  performDelete,
                });
              }}
              ratingOverrides={teamChangeRatingOverrides}
              onSetRating={(engineerId, skillId, next) => {
                setTeamChangeRatingOverrides(prev => {
                  const engMap = { ...(prev[engineerId] ?? {}) };
                  if (next === null) {
                    delete engMap[skillId];
                  } else {
                    engMap[skillId] = next;
                  }
                  return { ...prev, [engineerId]: engMap };
                });
              }}
              addedICIDs={
                new Set(teamChangeAddedICs.map(ic => ic.id))
              }
              departedICIDs={teamChangeDepartedIDs}
              onRenameDerivedContext={(compId, next) =>
                setTeamChangeAddedComps(prev =>
                  prev.map(c =>
                    c.id === compId ? { ...c, display: next } : c
                  )
                )
              }
              onDeleteDerivedContext={compId => {
                setTeamChangeAddedComps(prev =>
                  prev.filter(c => c.id !== compId)
                );
                setTeamChangeAddedSkills(prev => {
                  const next = { ...prev };
                  delete next[compId];
                  return next;
                });
              }}
              availableFunctions={view.business_functions}
              engineerSkillFunctions={teamChangeSkillFunctions}
              onToggleEngineerSkillFunction={(
                engId,
                skillId,
                fnId,
                defaultIds
              ) => {
                setTeamChangeSkillFunctions(prev => {
                  const engMap = { ...(prev[engId] ?? {}) };
                  const current = engMap[skillId] ?? defaultIds;
                  const next = current.includes(fnId)
                    ? current.filter(id => id !== fnId)
                    : [...current, fnId];
                  engMap[skillId] = next;
                  return { ...prev, [engId]: engMap };
                });
              }}
              onRenameIC={(id, next) =>
                setTeamChangeICRenames(prev => ({ ...prev, [id]: next }))
              }
              onDeleteIC={id => {
                // Added ICs are session-only stubs → fully remove. Existing
                // ICs come from the snapshot → mark them departed instead
                // (toggle, so a second click un-departs them).
                const isAdded = teamChangeAddedICs.some(ic => ic.id === id);
                if (isAdded) {
                  setTeamChangeAddedICs(prev =>
                    prev.filter(ic => ic.id !== id)
                  );
                  setTeamChangeICRenames(prev => {
                    const next = { ...prev };
                    delete next[id];
                    return next;
                  });
                  setTeamChangeRatingOverrides(prev => {
                    const next = { ...prev };
                    delete next[id];
                    return next;
                  });
                } else {
                  setTeamChangeDepartedIDs(prev => {
                    const next = new Set(prev);
                    if (next.has(id)) next.delete(id);
                    else next.add(id);
                    return next;
                  });
                }
              }}
            />
          </div>
        </div>
      ) : createMode ? (
        <div className='snap-roadmap snap-roadmap--create'>
          <div className='snap-roadmap__content'>
            <RoadmapResourceGrid
              project={createProject}
              ctx={ctx}
              functionsCount={view.business_functions.length}
              grouped={grouped}
              createMode
              onAddProfile={() =>
                setCreateRoles(prev => [
                  ...prev,
                  { id: `role-${prev.length + 1}`, display: 'Build' },
                ])
              }
              overlayCompsPool={overlayPool.compsPool}
              overlaySkillsPool={overlayPool.skillsPool}
              linkedCompCounts={linkedRefs.compCounts}
              linkedSkillCounts={linkedRefs.skillCounts}
              onAddDerivedContext={item => {
                // Picker dispatch: existing pool id → LINK (reuse the
                // id; auto-link any pool skills under that comp +
                // seed Competent on every role for them). Free-text
                // → MINT new + seed a placeholder skill so the row is
                // visible.
                if (item.id) {
                  if (createAddedComps.some(c => c.id === item.id)) return;
                  const compId = item.id;
                  setCreateAddedComps(prev => [
                    ...prev,
                    { id: compId, display: item.display },
                  ]);
                  const linkedSkills =
                    overlayPool.skillsPool[compId] ?? [];
                  const seededSkills =
                    linkedSkills.length > 0
                      ? linkedSkills.map(s => ({
                          id: s.id,
                          display: s.display,
                        }))
                      : [
                          {
                            id: `${compId}__skill-1`,
                            display: 'New skill',
                          },
                        ];
                  setCreateAddedSkills(prev => ({
                    ...prev,
                    [compId]: seededSkills,
                  }));
                  // Seed Competent (2) requirements on every existing
                  // role for each linked skill so the matrix arrives
                  // populated.
                  setCreateRequirements(prev => {
                    const next = { ...prev };
                    for (const r of createRoles) {
                      next[r.id] = { ...(next[r.id] ?? {}) };
                      for (const s of seededSkills) {
                        next[r.id]![reqKey(compId, s.id)] = 2;
                      }
                    }
                    return next;
                  });
                } else {
                  const compId = `__new-comp-${Date.now()}__`;
                  setCreateAddedComps(prev => [
                    ...prev,
                    { id: compId, display: item.display },
                  ]);
                  setCreateAddedSkills(prev => ({
                    ...prev,
                    [compId]: [
                      { id: `${compId}__skill-1`, display: 'New skill' },
                    ],
                  }));
                }
              }}
              onAddSkill={(compId, item) => {
                // Linked pool skill → reuse id. Net-new → mint a
                // deterministic-but-unique id so seeded requirements
                // match the skill we just appended.
                const skillId =
                  item.id ?? `__new-skill-${compId}-${Date.now()}`;
                if (
                  (createAddedSkills[compId] ?? []).some(
                    s => s.id === skillId,
                  )
                )
                  return;
                setCreateAddedSkills(prev => ({
                  ...prev,
                  [compId]: [
                    ...(prev[compId] ?? []),
                    { id: skillId, display: item.display },
                  ],
                }));
                // Auto-seed every existing role with Competent (2). User
                // can change individual cells via the picker.
                setCreateRequirements(prev => {
                  const next = { ...prev };
                  for (const r of createRoles) {
                    next[r.id] = {
                      ...(next[r.id] ?? {}),
                      [reqKey(compId, skillId)]: 2,
                    };
                  }
                  return next;
                });
                // Auto-ungroup so the user can see the new row.
                setGrouped(false);
              }}
              onRenameSkill={(compId, skillId, nextName) => {
                setCreateAddedSkills(prev => ({
                  ...prev,
                  [compId]: (prev[compId] ?? []).map(s =>
                    s.id === skillId ? { ...s, display: nextName } : s
                  ),
                }));
              }}
              onDeleteSkill={(compId, skillId) => {
                const compDisplay =
                  createAddedComps.find(c => c.id === compId)?.display ?? '';
                const skillDisplay =
                  (createAddedSkills[compId] ?? []).find(
                    s => s.id === skillId,
                  )?.display ?? '';
                const performDelete = () => {
                  setCreateAddedSkills(prev => ({
                    ...prev,
                    [compId]: (prev[compId] ?? []).filter(
                      s => s.id !== skillId,
                    ),
                  }));
                  const key = reqKey(compId, skillId);
                  setCreateRequirements(prev => {
                    const next: typeof prev = {};
                    for (const [roleId, roleMap] of Object.entries(prev)) {
                      const filtered = { ...roleMap };
                      delete filtered[key];
                      next[roleId] = filtered;
                    }
                    return next;
                  });
                  setCreateDeprecated(prev => {
                    const next: typeof prev = {};
                    for (const [roleId, roleMap] of Object.entries(prev)) {
                      const filtered = { ...roleMap };
                      delete filtered[key];
                      next[roleId] = filtered;
                    }
                    return next;
                  });
                  setCreateSkillFunctions(prev => {
                    const next: typeof prev = {};
                    for (const [roleId, roleMap] of Object.entries(prev)) {
                      const filtered = { ...roleMap };
                      delete filtered[key];
                      next[roleId] = filtered;
                    }
                    return next;
                  });
                };
                maybeInterceptLinkedSkillDelete({
                  compId,
                  skillId,
                  compDisplay,
                  skillDisplay,
                  performDelete,
                });
              }}
              onDeleteRole={roleId => {
                setCreateRoles(prev => prev.filter(r => r.id !== roleId));
                // Drop the role's requirement + deprecation slices too.
                setCreateRequirements(prev => {
                  const next = { ...prev };
                  delete next[roleId];
                  return next;
                });
                setCreateDeprecated(prev => {
                  const next = { ...prev };
                  delete next[roleId];
                  return next;
                });
                setCreateSkillFunctions(prev => {
                  const next = { ...prev };
                  delete next[roleId];
                  return next;
                });
              }}
              availableFunctions={view.business_functions}
              skillFunctions={createSkillFunctions}
              onToggleSkillFunction={(roleId, compId, skillId, fnId, defaultIds) => {
                // Seed the explicit override from the default-from-comp
                // set on first touch, then add/remove the toggled id. Keyed
                // by (comp, skill) so a shared skill's assignment in one
                // derivedContext doesn't clobber its crediting in another.
                const key = reqKey(compId, skillId);
                setCreateSkillFunctions(prev => {
                  const roleMap = { ...(prev[roleId] ?? {}) };
                  const current = roleMap[key] ?? defaultIds;
                  const next = current.includes(fnId)
                    ? current.filter(id => id !== fnId)
                    : [...current, fnId];
                  roleMap[key] = next;
                  return { ...prev, [roleId]: roleMap };
                });
              }}
              onRenameRole={(roleId, next) =>
                setCreateRoles(prev =>
                  prev.map(r => (r.id === roleId ? { ...r, display: next } : r))
                )
              }
              onRenameDerivedContext={(compId, next) =>
                setCreateCompOverrides(prev => ({ ...prev, [compId]: next }))
              }
              onDeleteDerivedContext={compId => {
                // Only added comps reach this path (the grid gates on
                // is_overlay). Drop the comp, its skills, and any
                // session display override pinned to its id.
                setCreateAddedComps(prev =>
                  prev.filter(c => c.id !== compId)
                );
                setCreateAddedSkills(prev => {
                  const next = { ...prev };
                  delete next[compId];
                  return next;
                });
                setCreateCompOverrides(prev => {
                  const next = { ...prev };
                  delete next[compId];
                  return next;
                });
              }}
              deprecatedSkillIds={deprecatedSkillIds}
              onDeprecateDerivedContext={(compId, skillIds) => {
                // Mark every (role, skill) under the comp deprecated;
                // clear any numeric requirements that were on those
                // cells so the two states stay mutually exclusive (the
                // cell picker enforces the same invariant).
                setCreateDeprecated(prev => {
                  const next = { ...prev };
                  for (const role of createRoles) {
                    const roleMap = { ...(next[role.id] ?? {}) };
                    for (const skillId of skillIds) {
                      roleMap[reqKey(compId, skillId)] = true;
                    }
                    next[role.id] = roleMap;
                  }
                  return next;
                });
                setCreateRequirements(prev => {
                  const next: typeof prev = {};
                  for (const [roleId, roleMap] of Object.entries(prev)) {
                    const filtered = { ...roleMap };
                    for (const skillId of skillIds) {
                      delete filtered[reqKey(compId, skillId)];
                    }
                    next[roleId] = filtered;
                  }
                  return next;
                });
              }}
              onRestoreDerivedContext={(compId, skillIds) => {
                setCreateDeprecated(prev => {
                  const next: typeof prev = {};
                  for (const [roleId, roleMap] of Object.entries(prev)) {
                    const filtered = { ...roleMap };
                    for (const skillId of skillIds) {
                      delete filtered[reqKey(compId, skillId)];
                    }
                    next[roleId] = filtered;
                  }
                  return next;
                });
              }}
              onRestoreSkill={(compId, skillId) => {
                const key = reqKey(compId, skillId);
                setCreateDeprecated(prev => {
                  const next: typeof prev = {};
                  for (const [roleId, roleMap] of Object.entries(prev)) {
                    const filtered = { ...roleMap };
                    delete filtered[key];
                    next[roleId] = filtered;
                  }
                  return next;
                });
              }}
              onSetRequirement={(roleId, compId, skillId, level) => {
                // Deprecated and level are mutually exclusive — picking
                // one clears the other so the cell never has conflicting
                // states. `null` clears both (the "Clear" picker option).
                // Keyed by (comp, skill) so editing one derivedContext's cell
                // never touches a shared skill under another derivedContext.
                const key = reqKey(compId, skillId);
                setCreateRequirements(prev => {
                  const roleMap = { ...(prev[roleId] ?? {}) };
                  if (level === null || level === 'deprecated') {
                    delete roleMap[key];
                  } else {
                    roleMap[key] = level;
                  }
                  return { ...prev, [roleId]: roleMap };
                });
                setCreateDeprecated(prev => {
                  const roleMap = { ...(prev[roleId] ?? {}) };
                  if (level === 'deprecated') {
                    roleMap[key] = true;
                  } else {
                    delete roleMap[key];
                  }
                  return { ...prev, [roleId]: roleMap };
                });
              }}
              deprecatedMap={createDeprecated}
              onRenameProject={next => setCreateProjectName(next)}
            />
          </div>
        </div>
      ) : (
        // Always render the sidebar even when the org has no roadmaps /
        // projects / plans — the sidebar's "+ New project" and
        // "+ New team change" buttons are the only authoring entry
        // points after the plans migration (PR #140), so the old
        // "No roadmap projects yet" empty state would strand fresh
        // orgs with nothing to click. Each sidebar section + the main
        // content area already handle their own empty-data shape.
        <div className='snap-roadmap'>
          <ControlPanel title='Plan Composition'>
            <RoadmapSidebar
              view={view}
              state={state}
              dispatch={dispatch}
              onCreateProject={enterCreateProjectMode}
              onCreateTeamChange={enterTeamChangeMode}
              onEditPlan={handleEditPlan}
              onDeletePlan={handleDeletePlan}
              onCreateRoadmap={handleCreateRoadmap}
              onUpdateRoadmap={handleUpdateRoadmap}
              isSavingRoadmap={isSavingRoadmap}
            />
          </ControlPanel>

          <div className='snap-roadmap__content'>
            {/*
              Resource = the platform map (engineers × skill catalog).
              Impact + Diff layer overlays on top of that base when a
              roadmap / project / team-change is selected. With nothing
              selected Impact + Diff have no overlay to render, so
              they show a dedicated empty state prompting the user to
              add a project — the platform map stays one click away
              via the Resource toggle.

              Before any of that, a repo-less org has no platform map to
              build against, so we surface the same repo-connect empty
              state the Timeline view uses.
            */}
            {noRepos ? (
              <ConnectReposEmpty title='No activity yet' />
            ) : state.view === 'resource' ? (
              <RoadmapResourceGrid
                project={resourceProjectWithOverlay}
                ctx={ctx}
                functionsCount={view.business_functions.length}
                grouped={grouped}
              />
            ) : state.view === 'impact' ? (
              activeProject ? (
                <RoadmapImpactGrid
                  project={activeProject}
                  grouped={grouped}
                  showLibraries={showLibraries}
                />
              ) : (
                <EmptyOverlayPrompt
                  title='Pick a project to see its impact'
                  body='The Impact view overlays a project’s context × function demand on the platform map. Add or select a project to populate this view.'
                  onCreateProject={enterCreateProjectMode}
                />
              )
            ) : state.view === 'diff' ? (
              activeProject ? (
                <RoadmapDiffGrid
                  project={activeProject}
                  ctx={ctx}
                  grouped={grouped}
                  showLibraries={showLibraries}
                />
              ) : (
                <EmptyOverlayPrompt
                  title='Pick a project to see the diff'
                  body='The Diff view compares a project’s demand against the team’s supply, cell by cell. Add or select a project to populate this view.'
                  onCreateProject={enterCreateProjectMode}
                />
              )
            ) : null}
          </div>
        </div>
      )}

      {pendingDelete ? (
        <ConfirmDialog
          title={
            pendingDelete.kind === 'project'
              ? 'Delete project?'
              : 'Delete team change?'
          }
          body={
            pendingDelete.kind === 'project'
              ? `Permanently remove “${pendingDelete.display}”? Roadmaps that reference it will keep the reference until you update them.`
              : `Permanently remove “${pendingDelete.display}”? Roadmaps that reference it will keep the reference until you update them.`
          }
          confirmLabel={deletePlan.isPending ? 'Deleting…' : 'Delete'}
          isPending={deletePlan.isPending}
          onConfirm={confirmDelete}
          onCancel={() => {
            if (deletePlan.isPending) return;
            setPendingDelete(null);
          }}
        />
      ) : null}

      {pendingLinkedSkillDelete ? (
        <ConfirmDialog
          title='Delete this skill from every linked plan?'
          body={(() => {
            const list = pendingLinkedSkillDelete.affectedPlanDisplays;
            const intro =
              list.length === 0
                ? 'No other plans currently link this skill — it will be removed only from this plan.'
                : list.length === 1
                  ? `This skill is also linked in ${list[0]!}. Confirming will delete “${pendingLinkedSkillDelete.skillDisplay}” from both plans on save, along with any requirements / ratings / function assignments pinned to it.`
                  : `This skill is also linked in ${list.length} other plans (${list.join(', ')}). Confirming will delete “${pendingLinkedSkillDelete.skillDisplay}” from every linked plan on save, along with any requirements / ratings / function assignments pinned to it.`;
            return intro;
          })()}
          confirmLabel='Delete from all'
          onConfirm={() => {
            pendingLinkedSkillDelete.onConfirm();
            setPendingLinkedSkillDelete(null);
          }}
          onCancel={() => setPendingLinkedSkillDelete(null)}
        />
      ) : null}
    </section>
  );
}

/**
 * Modal confirmation prompt for destructive actions (delete plan).
 * Renders into the existing snapshot scaffold without portaling — the
 * fixed overlay positioning handles z-index and centering. Cancel +
 * confirm both close the dialog; the destructive button surfaces a
 * `Deleting…` label while the mutation is in flight.
 */
function ConfirmDialog({
  title,
  body,
  confirmLabel,
  isPending = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  isPending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className='snap-confirm-overlay'
      role='dialog'
      aria-modal='true'
      aria-labelledby='snap-confirm-title'
      onClick={(e) => {
        // Clicking the backdrop (not the dialog itself) cancels.
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className='snap-confirm-dialog'>
        <h2 id='snap-confirm-title' className='snap-confirm-dialog__title'>
          {title}
        </h2>
        <p className='snap-confirm-dialog__body'>{body}</p>
        <div className='snap-confirm-dialog__actions'>
          <button
            type='button'
            className='snap-confirm-dialog__cancel'
            onClick={onCancel}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type='button'
            className='snap-confirm-dialog__confirm'
            onClick={onConfirm}
            disabled={isPending}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Click-to-edit H1 used by the Create Project / Team Change scaffolds.
 * Click (or focus + Enter / Space) swaps the heading for a focused
 * input; Enter or blur commits, Escape cancels. The committed value
 * flows up via `onChange` so the parent's scaffold name state is the
 * single source of truth.
 */
function EditableH1({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  // Keep the draft in sync with external changes (e.g. `handleEditPlan`
  // hydrating the name when reopening a saved plan).
  useEffect(() => {
    setDraft(value);
  }, [value]);

  if (editing) {
    const commit = () => {
      const trimmed = draft.trim();
      setEditing(false);
      if (trimmed.length > 0 && trimmed !== value) {
        onChange(trimmed);
      } else {
        setDraft(value);
      }
    };
    return (
      <input
        autoFocus
        className='snap-h1 snap-h1__input'
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setEditing(false);
            setDraft(value);
          }
        }}
      />
    );
  }

  return (
    <h1
      className='snap-h1 snap-h1--editable'
      role='button'
      tabIndex={0}
      title='Click to rename'
      onClick={() => setEditing(true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setEditing(true);
        }
      }}
    >
      {value}
    </h1>
  );
}

/**
 * Empty-state CTA shown in the Impact / Diff view modes when the user
 * has no project selected yet. The Resource toggle is one click away
 * for the bare platform map; this view exists to nudge the user
 * towards adding a project so the overlay-style views have data to
 * render.
 */
function EmptyOverlayPrompt({
  title,
  body,
  onCreateProject,
}: {
  title: string;
  body: string;
  onCreateProject: () => void;
}) {
  // Reuse the shared empty-state surface so this matches every other
  // "nothing here yet" moment (centered dashed box, standard CTA button)
  // instead of the bespoke left-aligned layout it used to render.
  return (
    <EmptyState
      title={title}
      description={body}
      cta={{ label: '+ New project', onClick: onCreateProject }}
    />
  );
}

/**
 * Resolve which `ProjectView` to render given the active selection:
 *   - aggregate when a roadmap is selected with at least one project on,
 *     and no specific project is pinned via `state.projectId`;
 *   - the single project pinned via `state.projectId` otherwise;
 *   - null if nothing is selectable yet (empty view).
 */
function resolveActiveProject(
  view: ViewPayload,
  state: ReturnType<typeof initialRoadmapState>,
  projectViews: Map<string, ProjectView>
): ProjectView | null {
  if (state.selectedProjectIds.length === 0) return null;
  if (state.selectedProjectIds.length === 1) {
    return projectViews.get(state.selectedProjectIds[0]!) ?? null;
  }
  const rm = activeRoadmap(state, view.roadmaps);
  // Multi-project selection → aggregate. Display label uses the
  // roadmap name when one's active so the grid header reads
  // "<roadmap> (aggregate)"; freeform multi-select shows "Selection".
  return aggregateProjects(
    state.selectedProjectIds,
    projectViews,
    rm?.display ?? 'Selection',
  );
}

/**
 * Derive a URL-safe slug from a display name + short timestamp tail.
 *
 * The slug becomes the plan's id (`plan_<slug>` on the wire). Adding
 * the timestamp avoids 409s when the user saves "New project" twice
 * — they're free to rename later via `update_plan` (next slice).
 */
function scaffoldSlug(display: string): string {
  const base = display
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  const tail = Date.now().toString(36).slice(-6);
  return base ? `${base}-${tail}` : `plan-${tail}`;
}
